import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  ScaleControl,
} from "react-leaflet";
import MarkerCluster from "../components/MarkerCluster";
import { useNavigate } from "react-router-dom";
import MapFlyTo from "./../components/MapFlyTo";
import FullScreenButton from "./../components/FullScreenButton";
import MouseIndicator from "./../components/MouseIndicator";
import LockMapButton from "./../components/LockMapButton";
import AOIDrawControl from "./../components/AOIDrawControl";
import LeakSeverityFilter from "./components/LeakSeverityFilter";
import wellknown from "wellknown";
import LeafletRuler from "./../components/LeafletRuler";
import DropdownMenu from "../components/DropdownMenu";
import PlaceSearch from "../../components/PlaceSearch";
import { useAuth } from "../../../../AuthContext";
import {
  highlightFeature,
  canvasRenderer,
} from "../components/HightlightFeature";
import FileUpload from "../components/FileUpload";
import { Box, Typography, CircularProgress } from "@mui/material";

const LeakMap = ({
  data,
  selected,
  AOIGeoJSON,
  isDrawing,
  setCustomAOI,
  pipesGeoJSON,
  setSeverityFilter,
  filterCleared,
  setFilterCleared,
  isLoading,
}) => {
  const [isAOIActive, setAOIActive] = useState(false);
  const [isPipesActive, setPipesActive] = useState(true);
  const [uploadedGeoJSON, setUploadedGeoJSON] = useState(null);
  const navigate = useNavigate();
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [zoom, setZoom] = useState(11);
  const mapRef = useRef(null);
  const { userProfile } = useAuth();
  const [center, setCenter] = useState([0, 0]);

  useEffect(() => {
    if (userProfile.profile.center) {
      setCenter([
        userProfile.profile.center.coordinates[1],
        userProfile.profile.center.coordinates[0],
      ]);
    }
  }, [userProfile]);

  const handleLockToggle = (isLocked) => {
    setIsMapLocked(isLocked);
  };

  const buttons = [
    { type: "AOI", activeState: isAOIActive, setActiveState: setAOIActive },
    {
      type: "Pipes",
      activeState: isPipesActive,
      setActiveState: setPipesActive,
    },
  ];

  const onMarkerClick = (leak) => {
    navigate("/observations/" + leak.id);
  };

  useEffect(() => {
    if (selected) {
      //TODO: esto hay que cambiarlo, aqui me puede llegar info del marker o del leak
      const selectedCenter =
        selected.position?.coordinates || selected.geometry?.coordinates;
      if (!isMapLocked && !filterCleared) {
        setCenter([selectedCenter[1], selectedCenter[0]]);
      }
      setFilterCleared(false);
    }
  }, [selected, isMapLocked, filterCleared]);

  const handleDrawComplete = (geojson) => {
    geojson = wellknown.stringify(geojson.geometry);
    setCustomAOI(geojson);
  };

  const handleDrawDeleted = () => {
    setCustomAOI(false);
  };

  return (
    <Box className={`map-app-leak`} sx={{ position: "relative" }}>
      {(!data || data.length === 0) && (
        <Box
          sx={{
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: "50%",
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <Typography variant="h6">No map data available</Typography>
        </Box>
      )}
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "99%", borderRadius: "15px" }}
        ref={mapRef}
      >
        <PlaceSearch setCenter={setCenter} setZoom={setZoom} />
        <MouseIndicator />
        <ScaleControl position="bottomleft" />
        <LeafletRuler />
        <MapFlyTo center={center} zoom={zoom} isFlyToEnabled={!isMapLocked} />

        <Box className="map-button-container">
          <FullScreenButton />
          <LockMapButton map={mapRef.current} onLockToggle={handleLockToggle} />
          <FileUpload
            onFileLoad={setUploadedGeoJSON}
            setCenter={setCenter}
            setZoom={setZoom}
          />
          <DropdownMenu buttons={buttons} />
        </Box>

        <LayersControl position="bottomright">
          <LayersControl.BaseLayer name="Relief" checked>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              minZoom={4}
              maxZoom={30}
              maxNativeZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street Map">
            <TileLayer
              url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
              minZoom={4}
              maxZoom={30}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <LeakSeverityFilter
          setSeverityFilter={setSeverityFilter}
        />

        {isLoading ? (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {data && data.length > 0 && (
              <MarkerCluster
                points={data}
                selected={selected}
                onMarkerClicked={onMarkerClick}
              />
            )}

            <AOIDrawControl
              onDrawComplete={handleDrawComplete}
              isDrawing={isDrawing}
              onDrawDeleted={handleDrawDeleted}
              filterCleared={filterCleared}
            />

            {isPipesActive &&
              pipesGeoJSON?.map((pipe, index) => (
                <GeoJSON
                  key={index}
                  data={{
                    ...pipe,
                    object: "PIPE",
                    name: pipe.name,
                  }}
                  onEachFeature={highlightFeature}
                  renderer={canvasRenderer}
                  className={"pipe-geometry"}
                />
              ))}

            {AOIGeoJSON &&
              isAOIActive &&
              AOIGeoJSON.map((aoi, index) => {
                if (
                  aoi.properties.name === "Libya" ||
                  aoi.properties.name === "ALG"
                ) {
                  return null;
                }
                return (
                  <GeoJSON
                    key={index}
                    data={{
                      ...aoi,
                      object: "AOI",
                    }}
                    onEachFeature={highlightFeature}
                    className={"aoi-geometry"}
                  />
                );
              })}

            {uploadedGeoJSON && (
              <GeoJSON
                data={uploadedGeoJSON}
                onEachFeature={highlightFeature}
                renderer={canvasRenderer}
              />
            )}
          </>
        )}
      </MapContainer>
    </Box>
  );
};

export default LeakMap;
